// src/pages/production/PlanningTab.tsx
//
// Phase 2C-lite. Manual forecast entry (no Excel import — that lands in a
// separate PR). Given a forecast list, derives:
//   - required batches per recipe
//   - required total qty per ingredient item
//   - shortage = max(0, needed - onHand)
//   - cost to procure shortages
//
// On-hand comes from each Item's optional `onHand` field — edit it on the
// Items tab when modeling shortages. Without onHand the shortage equals
// the full requirement.

import { useEffect, useMemo, useState } from 'react';
import { Plus, X, AlertCircle, CalendarRange, ShoppingCart, Upload, Download } from 'lucide-react';
import Modal from '../../components/Modal';
import {
  getItems, getRecipes, getProducts, getForecast,
  saveForecastLine, deleteForecastLine, clearForecast,
} from '../../services/productionStorage';
import { planForecast } from '../../services/productionPlanning';
import {
  parseForecastWorkbook, applyForecastRows, downloadForecastTemplate,
  type ParsedForecastRow,
} from '../../services/productionExcel';
import type {
  ProductionItem, ProductionRecipe, ProductionProduct, ForecastLine,
} from '../../types/production';

interface PlanningTabProps {
  slug: string;
}

const INPUT_CLASS = 'px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder-slate-500 focus:border-orange-500/50 focus:outline-none';

export default function PlanningTab({ slug }: PlanningTabProps) {
  const [items, setItems] = useState<ProductionItem[]>([]);
  const [recipes, setRecipes] = useState<ProductionRecipe[]>([]);
  const [products, setProducts] = useState<ProductionProduct[]>([]);
  const [forecast, setForecast] = useState<ForecastLine[]>([]);

  const [newProductId, setNewProductId] = useState('');
  const [newQty, setNewQty] = useState('');
  const [excelPreview, setExcelPreview] = useState<{ rows: ParsedForecastRow[]; warnings: string[] } | null>(null);

  useEffect(() => {
    setItems(getItems(slug));
    setRecipes(getRecipes(slug));
    setProducts(getProducts(slug));
    setForecast(getForecast(slug));
  }, [slug]);

  const productsById = useMemo(() => {
    const m: Record<string, ProductionProduct> = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  const recipesById = useMemo(() => {
    const m: Record<string, ProductionRecipe> = {};
    for (const r of recipes) m[r.id] = r;
    return m;
  }, [recipes]);

  const itemsById = useMemo(() => {
    const m: Record<string, ProductionItem> = {};
    for (const i of items) m[i.id] = i;
    return m;
  }, [items]);

  const plan = useMemo(
    () => planForecast(forecast, productsById, recipesById, itemsById),
    [forecast, productsById, recipesById, itemsById],
  );

  function addLine() {
    const qty = parseFloat(newQty);
    if (!newProductId || !Number.isFinite(qty) || qty <= 0) return;
    saveForecastLine(slug, { productId: newProductId, qty });
    setForecast(getForecast(slug));
    setNewProductId('');
    setNewQty('');
  }

  function removeLine(id: string) {
    deleteForecastLine(slug, id);
    setForecast(getForecast(slug));
  }

  function clearAll() {
    if (!confirm('Clear the entire forecast?')) return;
    clearForecast(slug);
    setForecast([]);
  }

  function handleExcelPick() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const buf = await file.arrayBuffer();
      const parsed = parseForecastWorkbook(buf, products);
      setExcelPreview(parsed);
    };
    input.click();
  }

  function handleExcelConfirm() {
    if (!excelPreview) return;
    const linesToSave = applyForecastRows(excelPreview.rows);
    for (const line of linesToSave) {
      saveForecastLine(slug, line);
    }
    setForecast(getForecast(slug));
    setExcelPreview(null);
  }

  if (products.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="max-w-md text-center p-8 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <CalendarRange size={32} className="mx-auto text-slate-500 mb-3" />
          <h3 className="text-white font-semibold mb-1">No products to plan against</h3>
          <p className="text-sm text-slate-400">
            Add at least one Product on the Products tab — planning runs against finished SKUs.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">Planning</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Manual forecast → required batches + ingredient shortages. Phase 2 (lite): single horizon, no time-bucketing.
        </p>
      </div>

      {/* Forecast input */}
      <div className="mb-6 p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Forecast</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadForecastTemplate(products.map((p) => p.name))}
              className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
              title="Download Excel template with your products pre-filled"
            >
              <Download size={11} /> Template
            </button>
            <button
              onClick={handleExcelPick}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-orange-500/20 border border-orange-500/30 text-orange-200 text-xs hover:bg-orange-500/30"
              title="Bulk-add forecast lines from an Excel file"
            >
              <Upload size={11} /> Import Excel
            </button>
            {forecast.length > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-slate-400 hover:text-red-400"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-end mb-3">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-slate-400 mb-1">Product</label>
            <select
              value={newProductId}
              onChange={(e) => setNewProductId(e.target.value)}
              className={`${INPUT_CLASS} w-full`}
            >
              <option value="">— Pick product —</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <label className="block text-xs text-slate-400 mb-1">Qty (units)</label>
            <input
              type="number"
              min="0"
              step="1"
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addLine()}
              placeholder="0"
              className={`${INPUT_CLASS} font-mono w-full`}
            />
          </div>
          <button
            onClick={addLine}
            disabled={!newProductId || !newQty || parseFloat(newQty) <= 0}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm"
          >
            <Plus size={14} /> Add line
          </button>
        </div>

        {forecast.length === 0 ? (
          <p className="text-xs text-slate-500 italic">No forecast lines yet.</p>
        ) : (
          <div className="space-y-1">
            {forecast.map((line) => {
              const product = productsById[line.productId];
              return (
                <div key={line.id} className="flex items-center gap-3 px-3 py-2 rounded bg-slate-900/40">
                  <span className="text-white flex-1 text-sm">
                    {product ? product.name : <span className="text-amber-400">missing product</span>}
                  </span>
                  <span className="font-mono text-slate-200 text-sm">{line.qty.toLocaleString()} units</span>
                  <button
                    onClick={() => removeLine(line.id)}
                    className="p-1 rounded text-slate-500 hover:text-red-400"
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Excel preview modal */}
      {excelPreview && (
        <Modal onClose={() => setExcelPreview(null)} panelClassName="max-w-2xl">
          <h3 className="text-lg font-semibold text-white mb-2">Forecast Excel preview</h3>
          {excelPreview.warnings.length > 0 && (
            <div className="mb-3 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs space-y-1">
              {excelPreview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
            </div>
          )}
          {excelPreview.rows.length === 0 ? (
            <p className="text-sm text-slate-400 mb-6">No valid rows found. Check the column headers in row 1.</p>
          ) : (
            <>
              <p className="text-sm text-slate-400 mb-3">
                {excelPreview.rows.length} row(s) parsed —
                <span className="text-emerald-300 ml-1">
                  {excelPreview.rows.filter((r) => r.productMatch).length} matched
                </span>,
                <span className="text-amber-300 ml-1">
                  {excelPreview.rows.filter((r) => !r.productMatch).length} unmatched
                </span> (unmatched rows are skipped on import).
              </p>
              <div className="max-h-[40vh] overflow-y-auto rounded border border-slate-700/40">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-900">
                    <tr className="text-left text-slate-400">
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2">Match</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {excelPreview.rows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1 text-white">{r.productLabel}</td>
                        <td className="px-3 py-1 text-right font-mono text-slate-200">{r.qty}</td>
                        <td className="px-3 py-1">
                          {r.productMatch ? (
                            <span className="text-emerald-300">{r.productMatch.name}</span>
                          ) : (
                            <span className="text-amber-300">no match — skipped</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => setExcelPreview(null)}
              className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleExcelConfirm}
              disabled={excelPreview.rows.filter((r) => r.productMatch).length === 0}
              className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium"
            >
              Add {excelPreview.rows.filter((r) => r.productMatch).length} line(s)
            </button>
          </div>
        </Modal>
      )}

      {/* Plan results */}
      {forecast.length > 0 && (
        <div className="space-y-6">
          {plan.unresolved.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle size={14} />
                <span className="font-medium">{plan.unresolved.length} forecast line(s) unresolved</span>
              </div>
              <ul className="text-xs ml-6 list-disc">
                {plan.unresolved.map((u) => (
                  <li key={u.forecastLineId}>{u.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Required batches */}
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-2">Required batches per recipe</h3>
            {plan.requiredBatches.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nothing to make.</p>
            ) : (
              <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                      <th className="px-4 py-3 font-medium">Recipe</th>
                      <th className="px-4 py-3 font-medium text-right">Batches</th>
                      <th className="px-4 py-3 font-medium text-right">Total yield</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/40">
                    {plan.requiredBatches.map((b) => {
                      const recipe = recipesById[b.recipeId];
                      return (
                        <tr key={b.recipeId} className="hover:bg-slate-700/20">
                          <td className="px-4 py-3 text-white font-medium">{b.recipeName}</td>
                          <td className="px-4 py-3 text-right font-mono text-white">
                            {b.batchesNeeded.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-slate-300">
                            {b.totalRecipeYield.toFixed(2)} {recipe?.yieldUnit || ''}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Required items + shortage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-slate-300">Required ingredients</h3>
              {plan.totalProcurementCost > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-mono text-white px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200">
                  <ShoppingCart size={11} />
                  Procure ${plan.totalProcurementCost.toFixed(2)}
                </span>
              )}
            </div>
            {plan.requiredItems.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No ingredient demand.</p>
            ) : (
              <div className="rounded-2xl bg-slate-800/30 border border-slate-700/40 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-400 text-xs uppercase tracking-wider bg-slate-900/40">
                      <th className="px-4 py-3 font-medium">Item</th>
                      <th className="px-4 py-3 font-medium text-right">Needed</th>
                      <th className="px-4 py-3 font-medium text-right">On hand</th>
                      <th className="px-4 py-3 font-medium text-right">Shortage</th>
                      <th className="px-4 py-3 font-medium text-right">Procure cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/40">
                    {plan.requiredItems.map((ri) => (
                      <tr key={ri.itemId} className="hover:bg-slate-700/20">
                        <td className="px-4 py-3">
                          <span className="text-white font-medium">{ri.itemName}</span>
                          <span className="text-xs text-slate-500 ml-2">{ri.unit}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">{ri.qtyNeeded.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-300">{ri.onHand.toFixed(2)}</td>
                        <td className={`px-4 py-3 text-right font-mono ${ri.shortage > 0 ? 'text-amber-300' : 'text-emerald-400'}`}>
                          {ri.shortage.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-slate-200">
                          {ri.costToProcure > 0 ? `$${ri.costToProcure.toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
